/* eslint-disable camelcase */
import { KYC_VENDORS } from 'constants/constants';
import { SHOW_FIAT_VALUE_IN_BELL_ALERTS } from 'constants/featureFlag';
import routes from 'constants/routes';
import { timeDurations } from 'constants/timeformats';
import { getDateTime, getDiff, getTimestampInMS, timeNow, toUTC } from 'helpers/day';
import { addBaseURL, cropAfterDecimals } from 'helpers/utils';
import i18n from 'i18n/config';

import {
  KYC_VERIFIED,
  DEPOSIT,
  WITHDRAWAL,
  USER_BELL_ALERT,
  COMMON_BELL_ALERT,
  BELL_ALERT_TITLES,
} from '../partials/constants';
import { IBellAlert } from '../types';

function compare(bellAlert1: IBellAlert, bellAlert2: IBellAlert) {
  const currentUTCTime = toUTC(timeNow());
  const date1 = getDateTime(bellAlert1.start_date);
  const date2 = getDateTime(bellAlert2.start_date);
  const timeDifference1 = getDiff(date1, currentUTCTime, 'milliseconds');
  const timeDifference2 = getDiff(date2, currentUTCTime, 'milliseconds');
  if (timeDifference1 < timeDifference2) {
    return 1;
  }
  if (timeDifference1 > timeDifference2) {
    return -1;
  }
  return 0;
}

export function sortBellAlertsByTime(bellAlertsList: IBellAlert[]) {
  return bellAlertsList.sort(compare);
}

// dates must be in UTC
export function getDateDifference(preDate, postDate) {
  /** Date in Milliseconds(MS) according to the `Unix Epoch`
   * Referrence link: https://www.epochconverter.com/
   */

  const preDateInMS = getTimestampInMS(getDateTime(preDate));
  const postDateInMS = getTimestampInMS(getDateTime(postDate));
  const differnceInMS = postDateInMS - preDateInMS;

  let furtherProceed = true;
  let dateDifference = null;
  let timeUnit = null;

  timeDurations.forEach(duration => {
    if (differnceInMS > duration.milliseconds && furtherProceed) {
      dateDifference = getDiff(
        getDateTime(postDate),
        getDateTime(preDate),
        duration.unit
      );
      timeUnit = dateDifference > 1 ? duration.unit : duration.unit.slice(0, -1);
      furtherProceed = false;
    }
  });
  return {
    dateDifference,
    timeUnit,
  };
}

// Added strings without translations because other bell alerts also available in english language which comes from backend

type updatedCta = string | null;

const getCustomizedDetails = (
  category: string,
  meta_data: IBellAlert['meta_data'],
  title,
  ctaText,
  ctaLink,
  isKycDone: boolean,
  precision
) => {
  let updatedTitle = '';
  let updatedDescription = '';
  let updatedCtaLink = null;
  let updatedCtaText: updatedCta = null;

  // if category type is kyc_verified
  if (category === KYC_VERIFIED) {
    // kyc level 1 is for phone verification common for both indian and non-indian user
    if (meta_data.kyc_level === '1' && isKycDone) {
      updatedTitle = 'Congratulations your Mobile Number is now verified';
      updatedCtaLink = addBaseURL(routes.options_chain.trade);
      updatedCtaText = 'Trade Now';
    }

    if (meta_data.kyc_level === '1' && !isKycDone) {
      updatedTitle = 'Congratulations your Mobile Number is now verified';
      updatedDescription = 'Please complete your KYC to start trading';
      updatedCtaLink = addBaseURL(routes.account.identityVerification);
      updatedCtaText = 'Initiate KYC';
    }

    // if user is indian
    if (
      meta_data.vendor === KYC_VENDORS.idfy ||
      meta_data.vendor === KYC_VENDORS.hyperverge
    ) {
      updatedTitle = 'Congratulations your account is now verified';
      updatedDescription = 'Start your trading journey';
      updatedCtaLink = addBaseURL(routes.account.buy_crypto);
      updatedCtaText = 'Deposit Now';
    }

    // if user is non indian
    else {
      if (meta_data.kyc_level === '2') {
        updatedTitle = 'Congratulations Your Account is now verified';
        updatedDescription =
          'Start your trading journey. Upgrade your account to verified plus to get unlimited withdrawals.';
        updatedCtaLink = addBaseURL(routes.account.deposit);
        updatedCtaText = 'Deposit Now';
      }
      if (meta_data.kyc_level === '3') {
        updatedTitle = 'Congratulations Your Account is now verified plus';
        updatedDescription =
          'Start your trading journey with unlimited daily/lifetime withdrawals.';
        updatedCtaLink = addBaseURL(routes.account.deposit);
        updatedCtaText = 'Deposit Now';
      }
    }
  }

  if (category === DEPOSIT && meta_data.succesful_deposit) {
    let depositAmount = meta_data.succesful_deposit;
    let depositAsset = meta_data.asset;

    if (SHOW_FIAT_VALUE_IN_BELL_ALERTS && meta_data.fiat_amount && meta_data.fiat_asset) {
      depositAmount = meta_data.fiat_amount;
      depositAsset = meta_data.fiat_asset;
    }

    const croppedDepositAmount = cropAfterDecimals(
      Number(depositAmount),
      precision
    ) as string;

    updatedTitle = i18n.t('alerts:depositSuccesFull');
    updatedDescription = i18n.t('alerts:depositSuccesFullDescription', {
      depositAmount: croppedDepositAmount,
      depositAsset,
    });
  }

  if (category === WITHDRAWAL && (meta_data.amount || meta_data.succesful_withdrawal)) {
    let withdrawalAmount = meta_data.amount;
    let withdrawalAsset = meta_data.asset;

    if (SHOW_FIAT_VALUE_IN_BELL_ALERTS && meta_data.fiat_amount && meta_data.fiat_asset) {
      withdrawalAmount = meta_data.fiat_amount;
      withdrawalAsset = meta_data.fiat_asset;
    }

    if (meta_data.succesful_withdrawal) {
      withdrawalAmount = meta_data.succesful_withdrawal;
      withdrawalAsset = meta_data.asset;
    }

    const croppedWithdrawalAmount = cropAfterDecimals(
      Number(withdrawalAmount),
      precision
    ) as string;

    if (meta_data.title === BELL_ALERT_TITLES.WITHDRAWAL.INITIATED) {
      updatedTitle = i18n.t('alerts:withdrawalInitiated');
      updatedDescription = i18n.t('alerts:withdrawalInitiatedDescription', {
        withdrawalAmount: croppedWithdrawalAmount,
        withdrawalAsset,
      });
    }

    // This condition is for old bell alerts which sends succesful_withdrawal instead of amount
    const isBellAlertWithOldMetaData = !meta_data.title && meta_data.succesful_withdrawal;

    if (
      meta_data.title === BELL_ALERT_TITLES.WITHDRAWAL.SUCCESS ||
      isBellAlertWithOldMetaData
    ) {
      updatedTitle = i18n.t('alerts:withdrawalSuccess');
      updatedDescription = i18n.t('alerts:withdrawalSuccessDescription', {
        withdrawalAmount: croppedWithdrawalAmount,
        withdrawalAsset,
      });
    }
  }

  if (category !== WITHDRAWAL && category !== DEPOSIT && category !== KYC_VERIFIED) {
    updatedTitle = title;
    updatedDescription = meta_data.description || '';
    updatedCtaLink = ctaLink;
    updatedCtaText = ctaText;
  }

  const updatedDetails = {
    title: updatedTitle,
    meta_data: { ...meta_data, description: updatedDescription },
    cta_link: updatedCtaLink,
    cta_text: updatedCtaText,
  };

  return updatedDetails;
};

export const customizedBellAlert = (
  details: IBellAlert,
  isKycDone: boolean,
  assetPrecisionData
) => {
  const assetSymbol = details.meta_data.asset;
  let precision = 2;
  if (assetSymbol) {
    const assetProperties = assetPrecisionData.find(
      asset => asset.symbol === assetSymbol
    );
    if (assetProperties) {
      precision = assetProperties.minimumPrecision;
    }
  }
  return {
    ...details,
    ...getCustomizedDetails(
      details.category,
      details.meta_data,
      details.title,
      details.cta_text,
      details.cta_link,
      isKycDone,
      precision
    ),
  };
};

export const getBellAlertIdType = (isUserBellAlert: boolean) => {
  return isUserBellAlert ? USER_BELL_ALERT : COMMON_BELL_ALERT;
};
